import type { ActiveFocusArea, RecommendedFocusArea } from '@api/focusAreasApiHandler';
import { useFocusAreasHooks } from '@api/focusAreasApiHandler';
import { useUserActionsHooks } from '@api/userActionsApiHandler';
import LightBulbIcon from '@assets/images/icons/lightbulb-outlined.svg';
import { DEFAULT_V2_IMAGE_PATH } from '@config/questionnaire-v2-images.config';
import { KeyConfigurations } from '@enums/KeyConfigurations';
import { useAppDispatch } from '@hooks/useAppDispatch';
import { useAppSelector } from '@hooks/useAppSelector';
import { Alert, Box, Skeleton } from '@mui/material';
import { MotionImage, MotionText, showToast } from '@shared/atoms';
import {
  CurrentPrioritiesCard,
  LoadingBackdrop,
  RecommendedAreasCard,
  SystemButton,
} from '@shared/molecules';
import { useQueryClient } from '@tanstack/react-query';
import { ToastMessages } from 'constants/toastMessages';
import React, { useMemo, useState } from 'react';
import { RootState } from 'store';
import { setActiveFocusAreas, setNavigationStep } from 'store/Slices/QuestionnaireSliceV2';
import styles from './index.module.scss';

export const IndicatorIcon = () => (
  <svg
    width="20px"
    height="20px"
    viewBox="0 0 30 30"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M26.61 24.495C28.725 21.9 30 18.6 30 15C30 6.72 23.28 0 15 0C6.72 0 0 6.72 0 15C0 23.28 6.72 30 15 30C18.6 30 21.9 28.725 24.495 26.61C24.9 26.28 25.29 25.92 25.665 25.545C25.71 25.5 25.74 25.455 25.77 25.425C26.07 25.125 26.355 24.81 26.61 24.495ZM15 27C8.385 27 3 21.615 3 15C3 8.385 8.385 3 15 3C21.615 3 27 8.385 27 15C27 17.775 26.055 20.31 24.465 22.35L22.32 20.205C23.355 18.735 23.97 16.95 23.97 15.015C23.97 10.05 19.935 6.015 14.97 6.015C10.005 6.015 5.97 10.05 5.97 15.015C5.97 19.98 10.005 24.015 14.97 24.015C16.92 24.015 18.735 23.385 20.205 22.32L22.335 24.45C20.31 26.055 17.775 27 15 27ZM17.88 15.765C18.135 14.775 17.91 13.695 17.145 12.915L17.115 12.885C15.96 11.73 14.115 11.715 12.945 12.825C12.93 12.84 12.9 12.855 12.87 12.885C11.7 14.055 11.7 15.96 12.87 17.13L12.9 17.16C13.68 17.925 14.775 18.165 15.765 17.895L18.03 20.16C17.13 20.7 16.095 21.03 14.97 21.03C11.655 21.03 8.97 18.345 8.97 15.03C8.97 11.715 11.655 9.03 14.97 9.03C18.285 9.03 20.97 11.715 20.97 15.03C20.97 16.125 20.655 17.145 20.13 18.03L17.88 15.765Z"
      fill="#019DF2"
    />
  </svg>
);

export const getQuestionIcon = (iconUrl?: string) => {
  if (iconUrl) {
    return (
      <img
        src={`${DEFAULT_V2_IMAGE_PATH}/${iconUrl}`}
        alt=""
        width="20px"
        height="20px"
        aria-hidden="true"
      />
    );
  }

  return <IndicatorIcon />;
};

export const getScoreClass = (concernPercentage: number): string => {
  if (concernPercentage >= 80) return styles.poor;
  if (concernPercentage >= 60) return styles.moderate;
  if (concernPercentage >= 40) return styles.average;

  return styles.good;
};

export const convertRecommendedFocusArea = (area: RecommendedFocusArea) => ({
  id: area.question.id,
  category: area?.question?.label || 'Indicator',
  subCategory: area?.category?.questionnairesV2Categories?.name || '',
  title: area.questionnaire?.name || '',
  score: `${Math.round((100 - area.focusAreaConcernPercentage) / 20)}/5`,
  scoreClass: getScoreClass(area.focusAreaConcernPercentage),
  icon: getQuestionIcon(area.question.icon),
  priorityScore: area.focusAreaPriorityScore,
  concernPercentage: area.focusAreaConcernPercentage,
  questionV2Id: area.question.id,
  iconUrl: area.question?.icon ? `${DEFAULT_V2_IMAGE_PATH}/${area.question.icon}` : null,
});

export const convertActiveFocusArea = (area: ActiveFocusArea) => ({
  id: area.questionV2Id,
  category: area?.questionnairesV2Questions?.label || 'Indicator',
  subCategory: area?.category?.questionnairesV2Categories?.name || '',
  title: area?.questionnaire?.name || '',
  score: `${Math.round((100 - area.concernPercentage) / 20)}/5`,
  scoreClass: getScoreClass(area.concernPercentage),
  icon: getQuestionIcon(area.questionnairesV2Questions.icon),
  priorityScore: area.priorityScore,
  concernPercentage: area.concernPercentage,
  questionV2Id: area.questionnairesV2Questions.id,
  focusAreaId: area.id,
  iconUrl: area.questionnairesV2Questions?.icon
    ? `${DEFAULT_V2_IMAGE_PATH}/${area.questionnairesV2Questions.icon}`
    : null,
});

const RecommendedAreasSkeleton = () => (
  <div className={styles.priorityGrid}>
    {[...Array(4)].map((_, index) => (
      <Box key={index} className={styles.priorityCard}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Skeleton variant="circular" width={40} height={40} />
          <Box flex={1}>
            <Skeleton variant="text" width="60%" height={24} />
            <Skeleton variant="text" width="40%" height={20} />
          </Box>
        </Box>
        <Skeleton variant="text" width="80%" height={20} />
        <Skeleton variant="rectangular" width="100%" height={32} sx={{ mt: 2, borderRadius: 1 }} />
      </Box>
    ))}
  </div>
);

export const CurrentPrioritiesSkeleton = () => (
  <div className={styles.currentPrioritiesGrid}>
    {[...Array(3)].map((_, index) => (
      <Box key={index} className={styles.priorityCard}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Skeleton variant="circular" width={40} height={40} />
          <Box flex={1}>
            <Skeleton variant="text" width="70%" height={24} />
            <Skeleton variant="text" width="50%" height={20} />
          </Box>
          <Skeleton variant="circular" width={32} height={32} />
        </Box>
        <Skeleton variant="text" width="90%" height={20} />
      </Box>
    ))}
  </div>
);

interface FocusAreasProps {
  className?: string;
  handleStep?: () => void;
}

export const FocusAreas: React.FC<FocusAreasProps> = ({ className, handleStep }) => {
  const { questionnaire, createdAssessmentDetails } = useAppSelector(
    (state) => state.questionnaireV2 || {},
  );

  const [showAllAreas, setShowAllAreas] = useState(false);
  const [selectedPriorities, setSelectedPriorities] = useState<any[]>([]);
  const [removedPriorities, setRemovedPriorities] = useState<any[]>([]);
  const [hasInitialized, setHasInitialized] = useState(false);
  const dispatch = useAppDispatch();
  const companyConfig = useAppSelector((state: RootState) => state.companyConfig.data);
  const [submitting, setSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const {
    data: recommendedFocusAreasResponse,
    isLoading: isLoadingRecommended,
    error: recommendedError,
    refetch: refetchRecommended,
  } = useFocusAreasHooks.useRecommendedFocusAreas(createdAssessmentDetails?.id || 0);

  const {
    data: activeFocusAreasResponse,
    isLoading: isLoadingActive,
    error: activeError,
    refetch: refetchActive,
  } = useFocusAreasHooks.useActiveFocusAreas();

  const addFocusAreasMutation = useFocusAreasHooks.useAddFocusAreas();
  const removeFocusAreaMutation = useFocusAreasHooks.useRemoveFocusArea();
  const batchRemoveFocusAreaMutation = useFocusAreasHooks.useBatchRemoveFocusArea();
  const addActionsToUserActionMutation = useUserActionsHooks.useAddActionsToUserAction();

  const recommendedPriorities = useMemo(() => {
    if (recommendedFocusAreasResponse?.success) {
      const converted = recommendedFocusAreasResponse.responseObject.map(
        convertRecommendedFocusArea,
      );

      return showAllAreas ? converted : converted.slice(0, 4);
    }

    return [];
  }, [recommendedFocusAreasResponse?.responseObject, showAllAreas]);

  const activePriorities = useMemo(() => {
    if (!activeFocusAreasResponse?.responseObject) return [];

    return activeFocusAreasResponse.responseObject.map(convertActiveFocusArea);
  }, [activeFocusAreasResponse?.responseObject]);

  React.useEffect(() => {
    if (activePriorities.length > 0 && selectedPriorities.length === 0 && !hasInitialized) {
      setSelectedPriorities(activePriorities);
      setHasInitialized(true);
    }
  }, [activePriorities, selectedPriorities.length, hasInitialized]);

  const handleAddPriority = async (priority: any) => {
    if (selectedPriorities.find((p) => p.id === priority.id)) return;

    // Check if maximum limit of 10 is reached
    if (selectedPriorities.length >= 10) {
      showToast(ToastMessages.priorities.error.MAXIMUM_10_PRIORITIES, undefined, 'error');

      return;
    }

    try {
      if (createdAssessmentDetails?.id) {
        setSelectedPriorities([...selectedPriorities, priority]);
      } else {
        showToast(ToastMessages.priorities.error.CREATE_ASSESSMENT_FIRST, undefined, 'error');
      }
    } catch (error) {
      showToast(ToastMessages.common.UNEXPECTED_ERROR_TRY_AGAIN, undefined, 'error');
    }
  };

  const handleRemoveCurrentPriority = async (priorityId: number) => {
    const priority = selectedPriorities.find((p) => p.id === priorityId);

    if (priority?.focusAreaId) {
      setSelectedPriorities(selectedPriorities.filter((p) => p.id !== priorityId));
      setRemovedPriorities((prev) => {
        const exists = prev.some((p) => p.focusAreaId === priorityId);

        return exists ? prev : [...prev, priority];
      });
    } else {
      setSelectedPriorities(selectedPriorities.filter((p) => p.id !== priorityId));
    }
  };

  const handleRemoveRecommendedPriority = async (priorityId: number) => {
    const priority = selectedPriorities.find((p) => p.questionV2Id === priorityId);

    if (priority?.focusAreaId) {
      setSelectedPriorities(selectedPriorities.filter((p) => p.questionV2Id !== priorityId));
      setRemovedPriorities((prev) => {
        const exists = prev.some((p) => p.questionV2Id === priorityId);

        return exists ? prev : [...prev, priority];
      });
    } else {
      setSelectedPriorities(selectedPriorities.filter((p) => p.questionV2Id !== priorityId));
    }
  };

  const handleViewAllAreas = () => {
    setShowAllAreas(!showAllAreas);
  };

  const isPrioritySelected = useMemo(
    () => (priorityId: number) => selectedPriorities.some((p) => p.questionV2Id === priorityId),
    [selectedPriorities],
  );

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    action: (priority?: any) => void,
    priority?: any,
  ) => {
    if (event.key === KeyConfigurations.ENTER || event.key === KeyConfigurations.SPACE) {
      event.preventDefault();
      action(priority);
    }
  };

  const handleContinueToReport = async (priorities: any) => {
    try {
      setSubmitting(true);
      if (removedPriorities.length > 0) {
        const prioritiesToRemove = removedPriorities.filter((priority) => priority.focusAreaId);

        if (prioritiesToRemove.length > 0) {
          try {
            const removePromises = prioritiesToRemove.map((priority) =>
              batchRemoveFocusAreaMutation.mutateAsync(priority.focusAreaId),
            );

            await Promise.all(removePromises);

            await queryClient.invalidateQueries({ queryKey: ['active-focus-areas'] });

            setRemovedPriorities([]);
          } catch (error) {
            showToast(
              ToastMessages.priorities.error.ERROR_OCCURRED_WHEN_REMOVING,
              undefined,
              'error',
            );
          }
        }
      }

      if (priorities?.length === 0) {
        dispatch(setNavigationStep('reflection'));
      }

      if (priorities?.length > 0) {
        const focusAreas = priorities.map((priority: any) => ({
          questionV2Id: priority.questionV2Id,
          concernPercentage: priority.concernPercentage || 0,
          priorityScore: priority.priorityScore || 0,
        }));

        await addFocusAreasMutation.mutateAsync(
          {
            userAssessmentId: createdAssessmentDetails?.id || 0,
            focusAreas,
          },
          {
            onSuccess: async (dt) => {
              if (dt?.success) {
                const addedAreasV2Ids =
                  dt?.success && dt?.responseObject?.length > 0
                    ? dt?.responseObject?.map((fa) => fa.questionV2Id)
                    : [];

                const existingIds = activePriorities.map((ac) => ac.questionV2Id);
                const selectedNewIds = priorities.map(
                  (sNew: { questionV2Id: number }) => sNew.questionV2Id,
                );
                const newPriorityIdsOnly = selectedNewIds.filter(
                  (id: number) => !existingIds.includes(id),
                );

                showToast(
                  ToastMessages.priorities.success.PRIORITIES_SAVED_SUCCESSFULLY,
                  undefined,
                  'success',
                );
                if (questionnaire?.id && newPriorityIdsOnly?.length > 0) {
                  await addActionsToUserActionMutation.mutateAsync({
                    questionnaireId: questionnaire?.id,
                    focusAreas: newPriorityIdsOnly || [],
                    indicators: null,
                  });
                }

                if (selectedPriorities) {
                  if (selectedPriorities?.length > 0) {
                    if (companyConfig?.talkToAdvisor) {
                      dispatch(setNavigationStep('talkToAdvisor'));
                    } else {
                      dispatch(setNavigationStep('reflection'));
                    }
                  } else {
                    dispatch(setNavigationStep('reflection'));
                  }
                }
              } else if (dt?.message === 'All the focus areas are already added') {
                if (questionnaire?.id && priorities?.length > 0) {
                  // await addActionsToUserActionMutation.mutateAsync({
                  //   questionnaireId: questionnaire?.id,
                  //   focusAreas: [],
                  //   indicators: null,
                  // });
                }

                if (selectedPriorities) {
                  // const cleanSelectedPriorities: ConvertedFocusArea[] = selectedPriorities.map(
                  //   (priority) => ({
                  //     ...priority,
                  //     icon: undefined, // Remove the JSX
                  //   }),
                  // );
                  // await dispatch(setActiveFocusAreas(cleanSelectedPriorities));
                  if (selectedPriorities?.length > 0) {
                    if (companyConfig?.talkToAdvisor) {
                      dispatch(setNavigationStep('talkToAdvisor'));
                    } else {
                      dispatch(setNavigationStep('reflection'));
                    }
                  } else {
                    dispatch(setNavigationStep('reflection'));
                  }
                }

                showToast(
                  ToastMessages.priorities.info.ALL_THE_AREAS_ALREADY_ADDED,
                  undefined,
                  'info',
                );
              } else {
                showToast(dt?.message || ToastMessages.common.TRY_AGAIN, undefined, 'error');
              }
            },
            onError: (error) => {
              showToast(ToastMessages.common.FAILED_TO_SAVE_CHANGES, undefined, 'error');
            },
          },
        );
      }
    } catch (error) {
      showToast(ToastMessages.common.FAILED_TO_SAVE_CHANGES, undefined, 'error');
    } finally {
      const freshData = await refetchActive();
      if (freshData?.data?.success && freshData.data?.responseObject) {
        const updatedPriorities = freshData.data.responseObject.map(convertActiveFocusArea);

        const cleanSelectedPriorities: any[] = updatedPriorities.map((priority) => ({
          ...priority,
          icon: undefined, // Remove the JSX
        }));

        await dispatch(setActiveFocusAreas(cleanSelectedPriorities));
        await setSelectedPriorities(updatedPriorities);

        setTimeout(() => {
          setSubmitting(false);
        }, 1500);
      } else if (!freshData?.data?.success) {
        setTimeout(() => {
          setSubmitting(false);
        }, 1500);
      }
    }
  };

  return (
    <div className={styles.focusAreasContainer}>
      <MotionText as="h2" className={styles.title} text="Choose Your Priorities" />

      <MotionText
        as="text"
        className={styles.description}
        text="Based on your assessment, we've identified areas that could benefit from focus. Select
        more to include in your action plan."
      />

      <div className={styles.card}>
        <MotionText as="h4" className={styles.sectionTitle} text="Recommended priorities for you" />
        <MotionText
          as="text"
          className={styles.sectionDescription}
          text="We recommend these priorities for you based on your answers."
        />

        {recommendedError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load recommended focus areas. Please try refreshing.
          </Alert>
        )}

        {isLoadingRecommended ? (
          <RecommendedAreasSkeleton />
        ) : (
          <>
            <div className={styles.priorityGrid}>
              {recommendedPriorities.map((priority) => {
                const isSelected = isPrioritySelected(priority.questionV2Id);

                return (
                  <RecommendedAreasCard
                    key={priority.id}
                    priority={priority}
                    isSelected={isSelected}
                    handleRemovePriority={handleRemoveRecommendedPriority}
                    handleAddPriority={handleAddPriority}
                  />
                );
              })}
            </div>

            {recommendedFocusAreasResponse?.responseObject &&
              recommendedFocusAreasResponse.responseObject.length > 4 && (
                <div className={styles.viewAllContainer}>
                  <SystemButton
                    className={styles.viewAllButton}
                    onClick={handleViewAllAreas}
                    aria-expanded={showAllAreas}
                    variant="outlinedPrimarySkyBlue"
                  >
                    {showAllAreas ? 'Hide All Areas' : 'View All Areas'}
                  </SystemButton>
                </div>
              )}
          </>
        )}
      </div>

      <div className={styles.card}>
        <MotionText as="h4" className={styles.sectionTitle} text="Your current priorities" />
        <MotionText
          as="text"
          className={styles.sectionDescription}
          text="These areas are used to create your personalised action plan."
        />

        {activeError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load active focus areas. Please try refreshing.
          </Alert>
        )}

        {isLoadingActive ? (
          <>
            <CurrentPrioritiesSkeleton />
            <LoadingBackdrop text="Loading..." />
          </>
        ) : (
          <div className={styles.currentPrioritiesGrid}>
            {selectedPriorities.length > 0 ? (
              selectedPriorities.map((priority) => (
                <CurrentPrioritiesCard
                  key={priority.id}
                  priority={priority}
                  handleRemovePriority={handleRemoveCurrentPriority}
                />
              ))
            ) : (
              <div className={styles.emptyPrioritiesContainer}>
                <div className={styles.emptyContainer}>
                  <MotionImage
                    src={LightBulbIcon}
                    alt=""
                    aria-hidden="true"
                    className={styles.lightBulbIcon}
                  />
                  <div className={styles.emptyPrioritiesText}>
                    <MotionText as="h4" text="You haven't selected any priorities yet" />

                    <MotionText
                      as="text"
                      text="Selecting priorities helps us create a personalised action plan focused on
  what matters most to you."
                      className={styles.text}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className={styles.actionContainer}>
        <SystemButton
          aria-label="Continue to my report"
          onClick={() => {
            if (removedPriorities?.length === 0 && selectedPriorities?.length === 0) {
              dispatch(setNavigationStep('reflection'));
            } else {
              handleContinueToReport(selectedPriorities);
            }
          }}
          disabled={
            //TODO: uncomment this if we want to disable the button if no priorities are selected
            // selectedPriorities.length === 0 ||
            addFocusAreasMutation.isPending || removeFocusAreaMutation.isPending || submitting
          }
        >
          {addFocusAreasMutation.isPending || removeFocusAreaMutation.isPending || submitting
            ? 'Saving...'
            : 'Continue to My Report'}
        </SystemButton>
      </div>

      {(addFocusAreasMutation.isPending || removeFocusAreaMutation.isPending || submitting) && (
        <LoadingBackdrop text="Saving changes..." />
      )}
    </div>
  );
};

FocusAreas.defaultProps = {
  className: 'card',
  handleStep: () => undefined,
};
